/* ============================================================
   Antrobus Handcrafted — image zoom
   ------------------------------------------------------------
   Tap or click a product photo to open it full screen.
   Pinch or double-tap to zoom, drag to pan, swipe between
   photos in the same group.

   Self-contained: injects its own styles, binds by delegation
   so it also catches photos rendered later by page JS.
   Include once from layout/theme.liquid.
   ============================================================ */
(function () {
  'use strict';

  /* Which images open the viewer. */
  var SELECTORS = [
    '.detail-photo-slot img',
    '.gallery-strip-item img',
    '.gallery-grid img',
    '.product-photo img',
    '[data-zoomable] img',
    'img[data-zoomable]'
  ].join(',');

  var MAX_SCALE = 4;
  var DOUBLE_TAP_SCALE = 2.5;

  /* ── Styles ─────────────────────────────────────────────── */
  var css = document.createElement('style');
  css.textContent = [
    '.az-overlay{position:fixed;inset:0;z-index:100000;background:rgba(28,23,15,0.94);',
    'display:none;align-items:center;justify-content:center;touch-action:none;',
    'opacity:0;transition:opacity .22s ease;}',
    '.az-overlay.az-open{display:flex;opacity:1;}',
    '.az-stage{position:relative;width:100%;height:100%;overflow:hidden;',
    'display:flex;align-items:center;justify-content:center;}',
    '.az-img{max-width:100%;max-height:100%;object-fit:contain;display:block;',
    'transform-origin:0 0;will-change:transform;user-select:none;-webkit-user-drag:none;}',
    '.az-img.az-anim{transition:transform .25s ease;}',
    '.az-btn{position:absolute;background:rgba(253,249,244,0.92);border:none;cursor:pointer;',
    'color:#2C2416;border-radius:50%;width:44px;height:44px;display:flex;align-items:center;',
    'justify-content:center;z-index:2;-webkit-tap-highlight-color:transparent;}',
    '.az-btn:hover{background:#fff;}',
    '.az-close{top:16px;right:16px;}',
    '.az-prev{left:16px;top:50%;margin-top:-22px;}',
    '.az-next{right:16px;top:50%;margin-top:-22px;}',
    '.az-hint{position:absolute;bottom:22px;left:50%;transform:translateX(-50%);',
    'font-family:"DM Sans",sans-serif;font-size:10px;letter-spacing:0.2em;text-transform:uppercase;',
    'color:rgba(253,249,244,0.75);white-space:nowrap;pointer-events:none;',
    'transition:opacity .3s;}',
    '.az-count{position:absolute;top:24px;left:50%;transform:translateX(-50%);',
    'font-family:"DM Sans",sans-serif;font-size:10px;letter-spacing:0.2em;',
    'color:rgba(253,249,244,0.75);pointer-events:none;}',
    '@media (max-width:600px){.az-btn{width:40px;height:40px;}.az-prev,.az-next{display:none;}}',
    /* subtle affordance on the page itself */
    '.detail-photo-slot img{cursor:zoom-in;}'
  ].join('');
  document.head.appendChild(css);

  /* ── Overlay ────────────────────────────────────────────── */
  var overlay, stage, imgEl, hintEl, countEl, prevBtn, nextBtn;
  var group = [], index = 0;
  var scale = 1, tx = 0, ty = 0;
  var natW = 0, natH = 0, baseW = 0, baseH = 0;

  function icon(d) {
    return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
           'stroke-width="1.8" stroke-linecap="round"><path d="' + d + '"/></svg>';
  }

  function build() {
    overlay = document.createElement('div');
    overlay.className = 'az-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Image viewer');
    overlay.innerHTML =
      '<div class="az-stage"><img class="az-img" alt=""></div>' +
      '<div class="az-count"></div>' +
      '<button class="az-btn az-close" aria-label="Close">' + icon('M18 6 6 18M6 6l12 12') + '</button>' +
      '<button class="az-btn az-prev" aria-label="Previous image">' + icon('M15 18l-6-6 6-6') + '</button>' +
      '<button class="az-btn az-next" aria-label="Next image">' + icon('M9 18l6-6-6-6') + '</button>' +
      '<div class="az-hint"></div>';
    document.body.appendChild(overlay);

    stage   = overlay.querySelector('.az-stage');
    imgEl   = overlay.querySelector('.az-img');
    hintEl  = overlay.querySelector('.az-hint');
    countEl = overlay.querySelector('.az-count');
    prevBtn = overlay.querySelector('.az-prev');
    nextBtn = overlay.querySelector('.az-next');

    overlay.querySelector('.az-close').addEventListener('click', close);
    prevBtn.addEventListener('click', function (e) { e.stopPropagation(); step(-1); });
    nextBtn.addEventListener('click', function (e) { e.stopPropagation(); step(1); });

    // Note: no click-to-close handler here. The stage captures the
    // pointer for panning, which retargets the resulting click to the
    // stage — so a tap on the photo itself would look identical to a
    // tap on the backdrop. Closing is decided in the pointerup logic
    // below, by testing the tap position against the image bounds.

    bindGestures();
  }

  /* ── Transform helpers ──────────────────────────────────── */
  function apply(animate) {
    imgEl.classList.toggle('az-anim', !!animate);
    imgEl.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
    if (animate) setTimeout(function () { imgEl.classList.remove('az-anim'); }, 260);
  }

  /* Returns false if the image has no dimensions yet — decoding may
     not have finished, and measuring then would give a base size of
     zero and pin the image off centre. */
  function measure() {
    var r = imgEl.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    baseW = r.width / scale;
    baseH = r.height / scale;
    return true;
  }

  function clamp() {
    var sr = stage.getBoundingClientRect();
    var w = baseW * scale, h = baseH * scale;

    // The stage centres the image with flexbox, so the element already
    // sits at this offset before any transform. tx/ty are relative to
    // that, not to the stage corner.
    var offX = (sr.width  - baseW) / 2;
    var offY = (sr.height - baseH) / 2;

    // Smaller than the stage: hold it centred.
    // Larger: keep its edges from drifting inside the frame.
    var minX, maxX, minY, maxY;
    if (w <= sr.width)  { minX = maxX = (sr.width  - w) / 2 - offX; }
    else                { minX = sr.width  - w - offX; maxX = -offX; }
    if (h <= sr.height) { minY = maxY = (sr.height - h) / 2 - offY; }
    else                { minY = sr.height - h - offY; maxY = -offY; }

    tx = Math.min(maxX, Math.max(minX, tx));
    ty = Math.min(maxY, Math.max(minY, ty));
  }

  function reset(animate, attempt) {
    scale = 1; tx = 0; ty = 0;
    imgEl.style.transform = '';
    if (!measure()) {
      // Wait for layout, but don't spin forever on a broken image.
      if ((attempt || 0) < 30) {
        requestAnimationFrame(function () { reset(animate, (attempt || 0) + 1); });
      }
      return;
    }
    clamp();
    apply(animate);
  }

  function zoomAt(newScale, cx, cy, animate) {
    newScale = Math.min(MAX_SCALE, Math.max(1, newScale));
    var sr = stage.getBoundingClientRect();

    // Measure the cursor relative to the image's own layout position,
    // not the stage corner — flexbox has already centred the element,
    // and ignoring that offset anchors the zoom to the wrong point.
    var offX = (sr.width  - baseW) / 2;
    var offY = (sr.height - baseH) / 2;
    var px = cx - sr.left - offX;
    var py = cy - sr.top  - offY;

    var ratio = newScale / scale;
    tx = px - (px - tx) * ratio;
    ty = py - (py - ty) * ratio;
    scale = newScale;
    clamp();
    apply(animate);
    updateHint();
  }

  function updateHint() {
    if (!hintEl) return;
    if (scale > 1.02) {
      hintEl.textContent = 'Drag to pan · Double-tap to fit';
    } else {
      hintEl.textContent = group.length > 1
        ? 'Pinch or double-tap to zoom · Swipe for next'
        : 'Pinch or double-tap to zoom';
    }
  }

  /* ── Gestures ───────────────────────────────────────────── */
  function bindGestures() {
    var pointers = new Map();
    var startDist = 0, startScale = 1, startMid = null;
    var panning = false, lastX = 0, lastY = 0;
    var downX = 0, downY = 0, downT = 0;
    var lastTapT = 0, lastTapX = 0, lastTapY = 0;

    function mid() {
      var pts = [...pointers.values()];
      return { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
    }
    function dist() {
      var pts = [...pointers.values()];
      return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    }

    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      stage.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.size === 2) {
        startDist = dist();
        startScale = scale;
        startMid = mid();
        panning = false;
      } else if (pointers.size === 1) {
        panning = true;
        lastX = e.clientX; lastY = e.clientY;
        downX = e.clientX; downY = e.clientY; downT = Date.now();
      }
    });

    stage.addEventListener('pointermove', function (e) {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.size === 2 && startDist > 0) {
        var m = mid();
        zoomAt(startScale * (dist() / startDist), m.x, m.y, false);
        return;
      }
      if (panning && scale > 1.02) {
        tx += e.clientX - lastX;
        ty += e.clientY - lastY;
        lastX = e.clientX; lastY = e.clientY;
        clamp();
        apply(false);
      }
    });

    function up(e) {
      if (!pointers.has(e.pointerId)) return;
      var wasSingle = pointers.size === 1;
      pointers.delete(e.pointerId);
      if (pointers.size < 2) startDist = 0;

      if (!wasSingle) { panning = pointers.size === 1; return; }
      panning = false;

      var dx = e.clientX - downX, dy = e.clientY - downY;
      var dt = Date.now() - downT;
      var moved = Math.hypot(dx, dy);

      // Swipe between images when not zoomed in
      if (scale <= 1.02 && group.length > 1 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        step(dx < 0 ? 1 : -1);
        return;
      }

      if (moved < 12 && dt < 400) {
        var now = Date.now();

        // Double tap / double click toggles zoom
        if (now - lastTapT < 350 && Math.hypot(e.clientX - lastTapX, e.clientY - lastTapY) < 40) {
          lastTapT = 0;
          if (scale > 1.02) reset(true);
          else zoomAt(DOUBLE_TAP_SCALE, e.clientX, e.clientY, true);
          return;
        }
        lastTapT = now; lastTapX = e.clientX; lastTapY = e.clientY;

        // A single tap outside the photo closes, but only once the
        // second tap of a possible double-tap can no longer arrive.
        if (scale <= 1.02) {
          var r = imgEl.getBoundingClientRect();
          var outside = e.clientX < r.left || e.clientX > r.right ||
                        e.clientY < r.top  || e.clientY > r.bottom;
          if (outside) {
            var stamp = lastTapT;
            setTimeout(function () { if (lastTapT === stamp) close(); }, 360);
          }
        }
      }
    }

    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', up);

    stage.addEventListener('wheel', function (e) {
      e.preventDefault();
      zoomAt(scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX, e.clientY, false);
    }, { passive: false });

    document.addEventListener('keydown', function (e) {
      if (!overlay || !overlay.classList.contains('az-open')) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    });

    window.addEventListener('resize', function () {
      if (overlay && overlay.classList.contains('az-open')) reset(false);
    });
  }

  /* ── Open / close / navigate ────────────────────────────── */
  function show(i) {
    index = (i + group.length) % group.length;
    var src = group[index].currentSrc || group[index].src;
    imgEl.src = src;
    imgEl.alt = group[index].alt || '';
    natW = group[index].naturalWidth;
    natH = group[index].naturalHeight;

    countEl.textContent = group.length > 1 ? (index + 1) + ' / ' + group.length : '';
    prevBtn.style.display = nextBtn.style.display = group.length > 1 ? '' : 'none';

    // Measure on load as well as now — a cached image reports
    // complete immediately but may not have been laid out yet.
    imgEl.onload = function () { reset(false); };
    reset(false);
    updateHint();
  }

  function step(dir) {
    if (group.length < 2) return;
    show(index + dir);
  }

  function open(img) {
    if (!overlay) build();

    // Group = sibling images inside the same container
    var container = img.closest('.detail-photos, .gallery-strip, .gallery-grid, [data-zoom-group]');
    group = container
      ? [].slice.call(container.querySelectorAll('img')).filter(function (n) { return n.src; })
      : [img];
    if (group.indexOf(img) === -1) group = [img];

    show(group.indexOf(img));
    overlay.classList.add('az-open');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    if (!overlay) return;
    overlay.classList.remove('az-open');
    document.body.style.overflow = '';
    imgEl.src = '';
  }

  /* ── Delegated binding (works for JS-rendered photos) ───── */
  document.addEventListener('click', function (e) {
    var img = e.target.closest ? e.target.closest('img') : null;
    if (!img || !img.matches(SELECTORS)) return;
    if (img.closest('a')) return;               // leave real links alone
    if (overlay && overlay.contains(img)) return;
    e.preventDefault();
    open(img);
  });

  window.AntrobusZoom = { open: open, close: close };
})();


/* ============================================================
   Photo strip affordance
   ------------------------------------------------------------
   On mobile the product photos become a horizontal strip. With
   one photo filling the frame there was nothing to show a second
   existed. Adds position dots beneath the strip, and a one-time
   hint, so it reads as a carousel.
   ============================================================ */
(function () {
  'use strict';

  var MOBILE = '(max-width: 900px)';

  function slotsOf(strip) {
    return [].slice.call(strip.querySelectorAll('.detail-photo-slot'));
  }

  function build(strip) {
    var slots = slotsOf(strip);
    if (slots.length < 2) { teardown(strip); return; }
    if (!window.matchMedia(MOBILE).matches) { teardown(strip); return; }
    if (strip.nextElementSibling && strip.nextElementSibling.classList.contains('az-dots')) {
      update(strip);
      return;
    }

    var dots = document.createElement('div');
    dots.className = 'az-dots';
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Photos');

    slots.forEach(function (slot, i) {
      var b = document.createElement('button');
      b.className = 'az-dot';
      b.type = 'button';
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-label', 'Photo ' + (i + 1) + ' of ' + slots.length);
      b.addEventListener('click', function () {
        strip.scrollTo({ left: slot.offsetLeft - strip.offsetLeft, behavior: 'smooth' });
      });
      dots.appendChild(b);
    });

    var hint = document.createElement('div');
    hint.className = 'az-photo-hint';
    hint.textContent = 'Swipe for more · Tap to zoom';

    strip.insertAdjacentElement('afterend', hint);
    strip.insertAdjacentElement('afterend', dots);

    strip.addEventListener('scroll', function () {
      if (strip._azTick) return;
      strip._azTick = true;
      requestAnimationFrame(function () { strip._azTick = false; update(strip); });
    }, { passive: true });

    update(strip);
  }

  function update(strip) {
    var dots = strip.nextElementSibling;
    if (!dots || !dots.classList.contains('az-dots')) return;
    var slots = slotsOf(strip);
    var mid = strip.scrollLeft + strip.clientWidth / 2;

    var best = 0, bestDist = Infinity;
    slots.forEach(function (s, i) {
      var c = s.offsetLeft - strip.offsetLeft + s.offsetWidth / 2;
      var d = Math.abs(c - mid);
      if (d < bestDist) { bestDist = d; best = i; }
    });

    [].slice.call(dots.children).forEach(function (d, i) {
      d.classList.toggle('is-active', i === best);
      d.setAttribute('aria-selected', i === best ? 'true' : 'false');
    });
  }

  function teardown(strip) {
    var n = strip.nextElementSibling;
    while (n && (n.classList.contains('az-dots') || n.classList.contains('az-photo-hint'))) {
      var next = n.nextElementSibling;
      n.remove();
      n = next;
    }
  }

  function scan() {
    [].slice.call(document.querySelectorAll('.detail-photos')).forEach(build);
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    scan();

    // The detail panel is rendered by page JS when a design is
    // chosen, so watch for it appearing rather than binding once.
    if (window.MutationObserver) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; scan(); });
      }).observe(document.body, { childList: true, subtree: true });
    }

    window.addEventListener('resize', scan);
  });
})();